import {useCallback,useEffect,useMemo,useState} from 'react';
import {useAppI18n} from '../../lib/appI18n';
import {
  harnessProviderSettingsApi,
  unlockHarnessProviderAuthority,
  type CatalogModel,
  type ProviderSettings,
  type ProviderSettingsSnapshot,
  type ProviderMutation,
} from '../../lib/harnessProviderSettingsApi';
import './harnessProviderSettings.css';

type Busy = 'load'|'save'|'remove'|'test'|'sync'|null;
type Modal = {kind:'key'|'remove';provider:ProviderSettings}|null;
type Info = {tone:'ok'|'error';message:string;code?:string}|null;

function errorInfo(error:unknown):Info{
  const message=error instanceof Error?error.message:String(error);
  const lower=message.toLowerCase();

  if(/provider_auth_failed|http_401|http_403/.test(lower))return {tone:'error',message:'hps.error.auth',code:message};
  if(/provider_credential_missing/.test(lower))return {tone:'error',message:'hps.error.noKey',code:message};
  if(/provider_catalog_(network|temporarily)|network_error/.test(lower))return {tone:'error',message:'hps.error.network',code:message};
  if(/provider_catalog_payload|provider_catalog_http/.test(lower))return {tone:'error',message:'hps.error.catalog',code:message};
  if(/provider_secret_master_key|master_key/.test(lower))return {tone:'error',message:'hps.error.masterKey',code:message};
  if(/step_up|authority_step_up|passkey/.test(lower))return {tone:'error',message:'hps.error.stepUp',code:message};
  if(/authority_denied|permission|harness_access_denied|identity_mismatch|tenant_mismatch/.test(lower))return {tone:'error',message:'hps.error.permission',code:message};

  return {tone:'error',message:'hps.error.generic',code:message};
}

function substitution(template:string,values:Record<string,string|number>):string{
  return Object.entries(values).reduce((text,[key,value])=>text.replace(`{${key}}`,String(value)),template);
}

function toneForSync(state:string|undefined):string{
  if(state==='SUCCESS')return 'ok';
  if(state==='FAILED')return 'error';
  return 'off';
}

export default function HarnessProviderSettings(){
  const {t,formatDate,formatNumber}=useAppI18n();
  const[snapshot,setSnapshot]=useState<ProviderSettingsSnapshot|null>(null);
  const[models,setModels]=useState<CatalogModel[]>([]);
  const[busy,setBusy]=useState<Busy>('load');
  const[info,setInfo]=useState<Info>(null);
  const[modal,setModal]=useState<Modal>(null);
  const[apiKey,setApiKey]=useState('');
  const[reason,setReason]=useState('');
  const[modelFilter,setModelFilter]=useState<string>('');

  const load=useCallback(async()=>{
    setBusy('load');
    try{
      const [nextSnapshot,nextModels]=await Promise.all([
        harnessProviderSettingsApi.snapshot(),
        harnessProviderSettingsApi.models(),
      ]);
      setSnapshot(nextSnapshot);
      setModels(nextModels.models);
    }catch(error){
      setInfo(errorInfo(error));
    }finally{
      setBusy(null);
    }
  },[]);

  useEffect(()=>{void load();},[load]);

  const visibleModels=useMemo(
    ()=>models.filter(model=>!modelFilter||model.provider_key===modelFilter),
    [models,modelFilter],
  );

  const openKey=(provider:ProviderSettings)=>{
    setApiKey('');
    setReason(t('hps.defaultReason'));
    setInfo(null);
    setModal({kind:'key',provider});
  };

  const openRemove=(provider:ProviderSettings)=>{
    setReason(t('hps.defaultReason'));
    setInfo(null);
    setModal({kind:'remove',provider});
  };

  const closeModal=()=>{
    if(busy==='save'||busy==='remove')return;
    setApiKey('');
    setModal(null);
  };

  const perform=async(
    action:Exclude<Busy,null>,
    successKey:string,
    fn:ProviderMutation,
    closeAfter=false,
    inspect?:(value:unknown)=>Info|null,
  )=>{
    setBusy(action);
    setInfo(null);
    try{
      await unlockHarnessProviderAuthority();
      const result=await fn();
      setInfo(inspect?.(result)??{tone:'ok',message:successKey});
      if(closeAfter){
        setApiKey('');
        setModal(null);
      }
      await load();
    }catch(error){
      setInfo(errorInfo(error));
    }finally{
      setBusy(null);
    }
  };

  const save=()=>{
    if(!modal||modal.kind!=='key')return;
    if(apiKey.trim().length<8||reason.trim().length<5)return;
    void perform('save','hps.saved',()=>harnessProviderSettingsApi.saveKey(
      modal.provider.provider_key,
      apiKey,
      reason.trim(),
    ),true,value=>{
      const sync=(value as {result?:{catalog_sync?:{state?:string;reason?:string}}})?.result?.catalog_sync;
      if(sync?.state==='SUCCESS')return null;
      return {
        tone:'error',
        message:'hps.savedSyncFailed',
        code:String(sync?.reason||'catalog_sync_failed'),
      };
    });
  };

  const remove=()=>{
    if(!modal||modal.kind!=='remove')return;
    if(reason.trim().length<5)return;
    void perform('remove','hps.removed',()=>harnessProviderSettingsApi.removeKey(
      modal.provider.provider_key,
      reason.trim(),
    ),true);
  };

  const syncLabel=(provider:ProviderSettings)=>{
    const sync=provider.catalog.last_sync;

    if(!sync)return t('hps.syncNever');
    if(sync.state==='SUCCESS')return t('hps.syncSuccess');
    if(sync.state==='FAILED')return t('hps.syncFailed');
    return sync.state;
  };

  return <main className="hps-page">
    <header className="hps-header">
      <div>
        <p className="hps-eyebrow">{t('hps.eyebrow')}</p>
        <h1>{t('hps.title')}</h1>
        <p className="hps-lead">{t('hps.description')}</p>
      </div>
      <div className="hps-header-actions">
        <a className="hps-secondary" href="/harness">{t('hps.back')}</a>
        <button type="button" className="hps-secondary" disabled={Boolean(busy)} onClick={()=>void load()}>{t('hps.refresh')}</button>
      </div>
    </header>

    <section className="hps-note" aria-labelledby="hps-security-title">
      <h2 id="hps-security-title">{t('hps.securityTitle')}</h2>
      <p>{t('hps.securityBody')}</p>
      <p>{t('hps.stepUpNote')}</p>
      {snapshot&&!snapshot.secret_master_key_ready&&<p className="hps-warning" role="alert">{t('hps.masterKeyMissing')}</p>}
    </section>

    {info&&<div className={`hps-notice ${info.tone}`} role={info.tone==='error'?'alert':'status'}>
      <span>{t(info.message)}</span>
      {info.code&&<code>{info.code}</code>}
    </div>}

    {busy==='load'&&!snapshot&&<p className="hps-muted" role="status">{t('hps.loading')}</p>}

    <section className="hps-provider-grid" aria-label={t('hps.title')}>
      {snapshot?.providers.map(provider=>{
        const sync=provider.catalog.last_sync;
        return <article className="hps-provider" key={provider.provider_key}>
          <div className="hps-provider-head">
            <div>
              <p className="hps-eyebrow">{provider.base_url}</p>
              <h2>{provider.display_name}</h2>
            </div>
            <div className="hps-badges" aria-label={t('hps.title')}>
              <span className={`hps-badge ${provider.credential_configured?'ok':'off'}`}>{provider.credential_configured?t('hps.configured'):t('hps.notConfigured')}</span>
              <span className={`hps-badge ${provider.connection_status==='CONNECTED'?'ok':'off'}`}>{provider.connection_status==='CONNECTED'?t('hps.connected'):t('hps.disconnected')}</span>
            </div>
          </div>

          <dl className="hps-facts">
            <div><dt>{t('hps.fingerprint')}</dt><dd>{provider.credential_fingerprint_prefix??'—'}</dd></div>
            <div><dt>{t('hps.keyVersion')}</dt><dd>{provider.key_version??'—'}</dd></div>
            <div><dt>{t('hps.lastVerified')}</dt><dd>{provider.last_verified_at?formatDate(provider.last_verified_at):t('hps.never')}</dd></div>
            <div><dt>{t('hps.lastSync')}</dt><dd><span className={`hps-badge ${toneForSync(sync?.state)}`}>{syncLabel(provider)}</span>{sync?.finished_at&&<small>{formatDate(sync.finished_at)}</small>}</dd></div>
          </dl>

          <div className="hps-counts">
            <span>{substitution(t('hps.models'),{count:formatNumber(provider.catalog.total)})}</span>
            <span>{substitution(t('hps.freeCount'),{count:formatNumber(provider.catalog.free)})}</span>
            <span>{substitution(t('hps.paidCount'),{count:formatNumber(provider.catalog.paid)})}</span>
            <span>{substitution(t('hps.unknownCount'),{count:formatNumber(provider.catalog.unknown_cost)})}</span>
          </div>

          <div className="hps-actions">
            <button type="button" className="hps-primary" disabled={Boolean(busy)} onClick={()=>openKey(provider)}>{provider.credential_configured?t('hps.replaceKey'):t('hps.saveKey')}</button>
            <button type="button" className="hps-secondary" disabled={Boolean(busy)||!provider.credential_configured} onClick={()=>void perform('test','hps.tested',()=>harnessProviderSettingsApi.test(provider.provider_key))}>{busy==='test'?t('hps.testing'):t('hps.test')}</button>
            <button type="button" className="hps-secondary" disabled={Boolean(busy)||!provider.credential_configured} onClick={()=>void perform('sync','hps.synced',()=>harnessProviderSettingsApi.sync(provider.provider_key))}>{busy==='sync'?t('hps.syncing'):t('hps.sync')}</button>
            <button type="button" className="hps-danger" disabled={Boolean(busy)||!provider.credential_configured} onClick={()=>openRemove(provider)}>{t('hps.removeKey')}</button>
          </div>

          {sync?.error_code&&<p className="hps-warning">{t('hps.syncFailed')}: <code>{sync.error_code}</code></p>}
        </article>;
      })}
    </section>

    <section className="hps-catalog" aria-labelledby="hps-catalog-title">
      <div className="hps-catalog-head">
        <div>
          <h2 id="hps-catalog-title">{t('hps.catalogTitle')}</h2>
          <p className="hps-muted">{t('hps.catalogDescription')}</p>
        </div>
        <label className="hps-filter">
          <span>{t('hps.provider')}</span>
          <select value={modelFilter} onChange={event=>setModelFilter(event.target.value)}>
            <option value="">{t('hps.allProviders')}</option>
            {snapshot?.providers.map(provider=><option key={provider.provider_key} value={provider.provider_key}>{provider.display_name}</option>)}
          </select>
        </label>
      </div>

      {visibleModels.length===0?<p className="hps-muted">{t('hps.catalogEmpty')}</p>:<div className="hps-table-wrap">
        <table className="hps-table">
          <thead><tr>
            <th>{t('hps.model')}</th>
            <th>{t('hps.provider')}</th>
            <th>{t('hps.context')}</th>
            <th>{t('hps.cost')}</th>
            <th>{t('hps.selectable')}</th>
          </tr></thead>
          <tbody>
            {visibleModels.slice(0,500).map(model=><tr key={model.provider_key+':'+model.upstream_model}>
              <td><strong>{model.display_name||model.upstream_model}</strong><small>{model.upstream_model}</small></td>
              <td>{model.provider_key}</td>
              <td>{model.context_window?formatNumber(model.context_window):'—'}</td>
              <td><span className={`hps-badge ${model.cost_class==='FREE'?'ok':model.cost_class==='PAID'?'error':'off'}`}>{model.cost_class==='FREE'?t('hps.free'):model.cost_class==='PAID'?t('hps.paid'):t('hps.unknownCost')}</span></td>
              <td><span className={`hps-badge ${model.selectable?'ok':'off'}`}>{model.selectable?t('hps.selectable'):t('hps.notSelectable')}</span></td>
            </tr>)}
          </tbody>
        </table>
      </div>}
      <p className="hps-muted">{t('hps.applyNote')}</p>
    </section>

    {modal&&<div className="hps-modal" role="presentation">
      <section className="hps-dialog" role="dialog" aria-modal="true" aria-labelledby="hps-dialog-title" aria-describedby="hps-dialog-body">
        <h2 id="hps-dialog-title">{modal.kind==='key'?t('hps.keyLabel'):t('hps.confirmRemoveTitle')}</h2>
        <p id="hps-dialog-body" className="hps-muted">{modal.kind==='key'?t('hps.keyHint'):t('hps.confirmRemoveBody')}</p>
        <p className="hps-dialog-provider">{modal.provider.display_name}</p>

        {modal.kind==='key'&&<label className="hps-field">
          <span>{t('hps.keyLabel')}</span>
          <input type="password" autoComplete="off" spellCheck={false} value={apiKey} placeholder={t('hps.keyPlaceholder')} onChange={event=>setApiKey(event.target.value)} disabled={busy==='save'}/>
        </label>}

        <label className="hps-field">
          <span>{t('hps.reasonLabel')}</span>
          <input type="text" value={reason} placeholder={t('hps.reasonPlaceholder')} maxLength={1000} onChange={event=>setReason(event.target.value)} disabled={busy==='save'||busy==='remove'}/>
        </label>

        <div className="hps-dialog-actions">
          <button type="button" className="hps-secondary" disabled={busy==='save'||busy==='remove'} onClick={closeModal}>{t('hps.cancel')}</button>
          {modal.kind==='key'
            ?<button type="button" className="hps-primary" disabled={busy==='save'||apiKey.trim().length<8||reason.trim().length<5} onClick={save}>{busy==='save'?t('hps.saving'):t('hps.saveKey')}</button>
            :<button type="button" className="hps-danger" disabled={busy==='remove'||reason.trim().length<5} onClick={remove}>{busy==='remove'?t('hps.removing'):t('hps.confirmRemove')}</button>}
        </div>
      </section>
    </div>}
  </main>;
}
